<template>
  <b-modal
    :visible="visible"
    :title="isEdit ? 'Editar Cupom' : 'Novo Cupom'"
    @hide="close"
    @ok="save"
    ok-title="Salvar"
    cancel-title="Cancelar"
  >
    <b-form @submit.prevent="save">
      <!-- Code (disabled on edit) -->
      <b-form-group label="Código do Cupom *">
        <b-form-input
          v-model="form.code"
          :disabled="isEdit"
          required
          placeholder="Ex: DESCONTO20"
        />
      </b-form-group>

      <!-- Discount Type -->
      <b-form-group label="Tipo de Desconto *">
        <b-form-radio-group v-model="form.discount_type">
          <b-form-radio value="percentage">Percentual</b-form-radio>
          <b-form-radio value="fixed">Valor Fixo</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <!-- Discount Value -->
      <b-form-group :label="discountLabel">
        <b-form-input
          v-if="form.discount_type === 'percentage'"
          v-model.number="form.discount_value"
          type="number"
          required
          step="0.01"
          min="0"
          max="100"
          placeholder="Ex: 20"
        />
        <money
          v-else
          v-model="form.discount_value"
          v-bind="money"
          class="form-control"
          required
        />
      </b-form-group>

      <!-- Min Order Value -->
      <b-form-group label="Valor Mínimo do Pedido">
        <money
          v-model="form.min_order_value"
          v-bind="money"
          class="form-control"
          placeholder="Deixe vazio para sem mínimo"
        />
      </b-form-group>

      <!-- Max Discount Value -->
      <b-form-group label="Desconto Máximo">
        <money
          v-model="form.max_discount_value"
          v-bind="money"
          class="form-control"
          placeholder="Deixe vazio para sem limite"
        />
      </b-form-group>

      <!-- Stock (only on create) -->
      <b-form-group v-if="!isEdit" label="Estoque">
        <b-form-checkbox v-model="form.unlimited_stock" switch class="mb-2">
          {{ form.unlimited_stock ? 'Estoque ilimitado ativado' : 'Estoque ilimitado desativado' }}
        </b-form-checkbox>

        <b-form-group v-if="!form.unlimited_stock" class="mb-0">
          <p class="mb-2 font-weight-bold">Defina o estoque inicial do cupom</p>
          <b-input-group append="unidades">
            <b-form-input
              type="number"
              v-model.number="form.stock"
              min="0"
            />
          </b-input-group>
        </b-form-group>
      </b-form-group>

      <!-- Stock Management (only on edit) -->
      <b-form-group v-if="isEdit" label="Gerenciar Estoque">
        <div class="mb-2">
          <small class="text-muted">
            <strong>Estoque Atual:</strong> {{ currentStockDisplay }}
            <span v-if="coupon.reserved_stock > 0" class="ml-2 text-warning">
              ({{ coupon.reserved_stock }} reservado)
            </span>
          </small>
        </div>

        <b-form-checkbox v-model="form.unlimited_stock" switch class="mb-2">
          Estoque Ilimitado
        </b-form-checkbox>

        <div v-if="!form.unlimited_stock">
          <b-form-select v-model="form.stock_action" :options="stockActions" class="mb-2" />

          <b-form-input
            v-if="['add', 'sub'].includes(form.stock_action)"
            v-model.number="form.stock_quantity"
            type="number"
            min="1"
            :placeholder="stockActionPlaceholder"
            class="mb-2"
          />
        </div>
      </b-form-group>

      <!-- Date Range -->
      <b-form-group label="Período de Validade *">
        <div class="text-center">
          <calendar
            @input="updateValidityPeriod"
            :value="validityPeriod"
            :max-date="maxDate"
          />
        </div>
      </b-form-group>

      <!-- Batch Restrictions -->
      <b-form-group label="Restringir a Lotes (opcional)">
        <b-link
          v-b-toggle.batch-restrictions
          class="d-block"
        >
          <icon name="chevron-down"></icon>
          {{ form.batch_ids.length > 0 ? `${form.batch_ids.length} lote(s) selecionado(s)` : 'Clique para selecionar lotes' }}
        </b-link>

        <b-collapse id="batch-restrictions" class="mt-2">
          <b-input-group class="mb-3" size="sm">
            <b-form-input
              v-model="search.batch"
              placeholder="Pesquisa rápida"
              type="search"
            ></b-form-input>
            <b-input-group-append>
              <b-button size="sm" :disabled="!search.batch" @click="search.batch = ''">Limpar</b-button>
            </b-input-group-append>
          </b-input-group>

          <b-form-checkbox-group v-model="form.batch_ids" :options="batchesOptions" stacked>
          </b-form-checkbox-group>
        </b-collapse>
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script>
import Calendar from '@/components/Calendar'
import { Money } from 'v-money'

export default {
  name: 'CouponForm',
  components: {
    Calendar,
    Money
  },
  props: {
    visible: Boolean,
    coupon: Object,
    organizer: String
  },
  data() {
    return {
      form: {
        code: '',
        discount_type: 'percentage',
        discount_value: 0,
        min_order_value: null,
        max_discount_value: null,
        stock: null,
        starts_at: '',
        expires_at: '',
        batch_ids: [],
        unlimited_stock: false,
        stock_action: null,
        stock_quantity: null
      },
      batches: [],
      search: {
        batch: ''
      },
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2
      },
      stockActions: [
        { value: null, text: 'Selecione uma ação' },
        { value: 'add', text: 'Adicionar unidades' },
        { value: 'sub', text: 'Retirar unidades' },
        { value: 'zero', text: 'Zerar estoque' }
      ]
    }
  },
  computed: {
    isEdit() {
      return !!this.coupon
    },
    discountLabel() {
      return this.form.discount_type === 'percentage' ? 'Percentual de Desconto (%)' : 'Valor Fixo (R$)'
    },
    validityPeriod() {
      return {
        start: this.form.starts_at,
        end: this.form.expires_at
      }
    },
    maxDate() {
      const today = new Date()
      const oneYearFromNow = new Date(today)
      oneYearFromNow.setFullYear(today.getFullYear() + 1)
      return oneYearFromNow
    },
    batchesOptions() {
      let filtered = this.batches

      if (this.search.batch) {
        filtered = this.$_.filter(this.batches, b => {
          let searched = this.search.batch.toLowerCase()
          return b.name.toLowerCase().indexOf(searched) >= 0
        })
      }

      return this.$_.map(filtered, b => {
        return { text: b.name, value: b.id }
      })
    },
    currentStockDisplay() {
      if (!this.coupon) return '-'
      if (this.coupon.stock === null) return 'Ilimitado'
      return this.coupon.stock
    },
    stockActionPlaceholder() {
      if (this.form.stock_action === 'add') return 'Quantidade a adicionar'
      if (this.form.stock_action === 'sub') return 'Quantidade a retirar'
      return ''
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadBatches()
        if (this.coupon) {
          this.populateForm()
        } else {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    async loadBatches() {
      try {
        const response = await this.$http.get(`${process.env.VUE_APP_URL_APP}/b/organizers/${this.organizer}/events`)
        this.batches = response.body.batches
      } catch (error) {
        console.error('Error loading batches:', error)
      }
    },

    populateForm() {
      this.form = {
        code: this.coupon.code,
        discount_type: this.coupon.discount_type,
        discount_value: this.coupon.discount_type === 'percentage'
          ? this.coupon.discount_value / 100
          : this.coupon.discount_value / 100,
        min_order_value: this.coupon.min_order_value ? this.coupon.min_order_value / 100 : 0,
        max_discount_value: this.coupon.max_discount_value ? this.coupon.max_discount_value / 100 : 0,
        starts_at: this.formatApiToCalendar(this.coupon.starts_at),
        expires_at: this.formatApiToCalendar(this.coupon.expires_at),
        batch_ids: this.coupon.batch_ids || [],
        unlimited_stock: this.coupon.stock === null,
        stock_action: null,
        stock_quantity: null
      }
    },

    resetForm() {
      // Set default dates to today
      const today = new Date()
      const dd = String(today.getDate()).padStart(2, '0')
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const yyyy = today.getFullYear()
      const startDate = `${dd}/${mm}/${yyyy} 00:00`
      const endDate = `${dd}/${mm}/${yyyy} 23:59`

      this.form = {
        code: '',
        discount_type: 'percentage',
        discount_value: 0,
        min_order_value: 0,
        max_discount_value: 0,
        stock: null,
        starts_at: startDate,
        expires_at: endDate,
        batch_ids: [],
        unlimited_stock: false
      }
    },

    async save() {
      const url = this.isEdit
        ? `${process.env.VUE_APP_URL_APP}/b/organizers/${this.organizer}/coupons/${this.coupon.id}`
        : `${process.env.VUE_APP_URL_APP}/b/organizers/${this.organizer}/coupons`
      const method = this.isEdit ? 'put' : 'post'

      // Convert form values to API format
      const payload = {
        code: this.form.code,
        discount_type: this.form.discount_type,
        discount_value: this.form.discount_type === 'percentage'
          ? Math.round(this.form.discount_value * 100)
          : Math.round(this.form.discount_value * 100),
        min_order_value: this.form.min_order_value > 0 ? Math.round(this.form.min_order_value * 100) : null,
        max_discount_value: this.form.max_discount_value > 0 ? Math.round(this.form.max_discount_value * 100) : null,
        starts_at: this.formatCalendarToApi(this.form.starts_at),
        expires_at: this.formatCalendarToApi(this.form.expires_at),
        batch_ids: this.form.batch_ids
      }

      // Add stock logic
      if (this.isEdit) {
        // Edit mode: stock management with actions (mirrors update-batch.bs.go pattern)
        if (this.form.unlimited_stock) {
          // Set stock to null (unlimited)
          payload.stock = null
        } else if (this.form.stock_action === 'zero') {
          // Zero action doesn't need quantity - always sends 0
          payload.stock = 0
        } else if (this.form.stock_action && this.form.stock_quantity) {
          // Calculate delta based on action
          let delta = 0
          if (this.form.stock_action === 'add') {
            delta = this.form.stock_quantity
          } else if (this.form.stock_action === 'sub') {
            delta = -this.form.stock_quantity
          }
          payload.stock = delta
        }
      } else {
        // Create mode: initial stock value
        if (this.form.unlimited_stock) {
          payload.stock = null
        } else if (this.form.stock !== null && this.form.stock !== undefined) {
          payload.stock = this.form.stock
        }
      }

      try {
        await this.$http[method](url, payload)
        this.$bvToast.toast('Cupom salvo com sucesso', { variant: 'success' })
        this.$emit('saved')
      } catch (error) {
        const message = error.body?.message || 'Erro ao salvar cupom'
        this.$bvToast.toast(message, { variant: 'danger' })
      }
    },

    close() {
      this.$emit('close')
    },

    updateValidityPeriod(period) {
      this.form.starts_at = period.start
      this.form.expires_at = period.end
    },

    formatApiToCalendar(apiDatetime) {
      // Convert "2025-12-26 00:00:00" to "26/12/2025 00:00"
      const parts = apiDatetime.split(' ')
      const dateParts = parts[0].split('-')
      const time = parts[1].substring(0, 5) // HH:mm
      return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]} ${time}`
    },

    formatCalendarToApi(calendarDatetime) {
      // Convert "26/12/2025 00:00" to "2025-12-26 00:00:00"
      const parts = calendarDatetime.split(' ')
      const dateParts = parts[0].split('/')
      return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]} ${parts[1]}:00`
    }
  }
}
</script>
